import { useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { useDocumentTitle, useTid } from '../lib/hooks';
import { fmtDayMon } from '../lib/format';
import type { Invite, Member, RestaurantRow, Role, TenantInfo } from '../lib/types';
import { Avatar, Button, Field, Loading, Sheet, useConfirm } from '../components/ui';
import { useToast } from '../components/feedback';

const ROLE_DESC: Record<Exclude<Role, 'owner'>, string> = {
  admin: 'Gerencia restaurantes, relatórios e a equipe inteira.',
  nutricionista: 'Registra visitas, fotos e relatórios nos restaurantes liberados.',
  visualizador: 'Apenas consulta, sem editar.',
};
const PERM_DESC: Record<Role, string> = { owner: 'Acesso total', admin: 'Acesso total', nutricionista: 'Registra visitas e fotos', visualizador: 'Somente leitura' };
const short = (n: string) => n.split(' ').slice(0, 2).join(' ');

export function Team() {
  const tid = useTid();
  const info = useOutletContext<TenantInfo>();
  useDocumentTitle('Equipe');
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const [inviting, setInviting] = useState(false);
  const manage = info.can.manageMembers;

  const team = useQuery({ queryKey: ['members', tid], queryFn: () => api.get<{ members: Member[]; invites: Invite[] }>(`/api/t/${tid}/members`) });
  const rests = useQuery({ queryKey: ['restaurants', tid, ''], queryFn: () => api.get<RestaurantRow[]>(`/api/t/${tid}/restaurants`), enabled: manage });

  const refresh = () => qc.invalidateQueries({ queryKey: ['members', tid] });
  const patchMember = useMutation({
    mutationFn: ({ id, ...b }: { id: string; role?: string; restaurantIds?: string[] }) => api.patch(`/api/t/${tid}/members/${id}`, b),
    onSuccess: refresh,
    onError: (e) => { toast(errMsg(e), 'err'); refresh(); },
  });
  const patchInvite = useMutation({
    mutationFn: ({ id, ...b }: { id: string; role?: string; restaurantIds?: string[] }) => api.patch(`/api/t/${tid}/members/invites/${id}`, b),
    onSuccess: refresh,
    onError: (e) => toast(errMsg(e), 'err'),
  });
  // Reenviar = novo convite (o anterior é invalidado no servidor).
  const resend = useMutation({
    mutationFn: (i: Invite) => api.post(`/api/t/${tid}/members/invites`, { email: i.email, role: i.role, restaurantIds: i.restaurantIds }),
    onSuccess: (_d, i) => { refresh(); toast(`Convite reenviado para ${i.email}`); },
    onError: (e) => toast(errMsg(e), 'err'),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.del(`/api/t/${tid}/members/invites/${id}`),
    onSuccess: () => { refresh(); toast('Convite revogado'); },
  });
  const removeMember = useMutation({
    mutationFn: (id: string) => api.del(`/api/t/${tid}/members/${id}`),
    onSuccess: () => { refresh(); toast('Membro removido'); },
    onError: (e) => toast(errMsg(e), 'err'),
  });

  const toggle = (list: string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  const AccessChips = ({ ids, onChange, disabled }: { ids: string[] | 'all'; onChange: (ids: string[]) => void; disabled: boolean }) =>
    ids === 'all' ? (
      <span className="chip toggle on" style={{ display: 'inline-flex', alignItems: 'center', height: 30 }}>Todos os restaurantes</span>
    ) : (
      <div className="row wrap gap-6">
        {(rests.data ?? []).map((r) => (
          <button key={r.id} className={`chip toggle ${ids.includes(r.id) ? 'on' : ''}`} style={{ height: 30, fontSize: 12 }} disabled={disabled} aria-pressed={ids.includes(r.id)} onClick={() => onChange(toggle(ids, r.id))}>{short(r.nome)}</button>
        ))}
        {!manage && <span className="small">{ids.length} {ids.length === 1 ? 'restaurante' : 'restaurantes'}</span>}
        {manage && rests.data?.length === 0 && <span className="small">Cadastre restaurantes para liberar acesso.</span>}
      </div>
    );

  const canEditMember = (m: Member) => manage && !m.isMe && m.role !== 'owner' && (m.role !== 'admin' || info.role === 'owner');

  return (
    <div>
      <div className="row gap-10" style={{ marginBottom: 6 }}>
        <h1 className="h1 flex1">Equipe</h1>
        {manage && <Button variant="accent" size="sm" onClick={() => setInviting(true)}>Convidar</Button>}
      </div>
      <div className="sub" style={{ marginBottom: 18 }}>
        {manage ? 'Papel define o que a parceira pode fazer. A visibilidade é definida restaurante por restaurante.' : 'Somente administradoras desta plataforma alteram papéis e acessos.'}
      </div>

      {!team.data ? (
        <Loading rows={3} height={150} />
      ) : (
        <div className="grid-cards" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 340px), 1fr))' }}>
          {team.data.members.map((m) => (
            <div key={m.id} className="card" style={{ padding: 15 }}>
              <div className="row gap-10" style={{ gap: 11 }}>
                <Avatar name={m.nome} size={36} />
                <div className="flex1">
                  <div className="wrapline" style={{ fontSize: 14, fontWeight: 600 }}>{m.nome}{m.isMe && <span className="small" style={{ fontWeight: 400 }}> · você</span>}</div>
                  <div className="wrapline small">{m.email}</div>
                </div>
              </div>
              <div className="row wrap gap-10" style={{ marginTop: 13, alignItems: 'flex-end' }}>
                <div className="flex1" style={{ minWidth: 150 }}>
                  <div className="k" style={{ marginBottom: 4 }}>Papel</div>
                  {canEditMember(m) ? (
                    <select className="select" style={{ height: 38, fontSize: 13.5 }} value={m.role} onChange={(e) => patchMember.mutate({ id: m.id, role: e.target.value })} aria-label={`Papel de ${m.nome}`}>
                      {info.role === 'owner' && <option value="admin">Admin</option>}
                      <option value="nutricionista">Nutricionista</option>
                      <option value="visualizador">Visualizador</option>
                    </select>
                  ) : (
                    <div style={{ fontSize: 13.5, fontWeight: 500 }}>{m.roleLabel}</div>
                  )}
                </div>
                <div className="none" style={{ padding: '8px 11px', borderRadius: 9, background: 'var(--accent-soft)', fontSize: 12, color: 'var(--resolved)' }}>{PERM_DESC[m.role]}</div>
              </div>
              <div className="divider-top" style={{ marginTop: 12, paddingTop: 12 }}>
                <div className="k" style={{ marginBottom: 7 }}>Restaurantes visíveis</div>
                <AccessChips ids={m.restaurantIds} disabled={!canEditMember(m)} onChange={(ids) => patchMember.mutate({ id: m.id, restaurantIds: ids })} />
              </div>
              {canEditMember(m) && (
                <div style={{ marginTop: 10, textAlign: 'right' }}>
                  <button
                    className="link-btn muted u"
                    style={{ fontSize: 11.5 }}
                    onClick={async () => {
                      if (await confirm.ask({ title: `Remover ${m.nome}?`, message: 'A pessoa perde o acesso a esta plataforma. As visitas registradas por ela continuam no histórico.', cta: 'Remover', danger: true })) removeMember.mutate(m.id);
                    }}
                  >
                    remover da equipe
                  </button>
                </div>
              )}
            </div>
          ))}
          {team.data.invites.map((i) => (
            <div key={i.id} className="card" style={{ padding: 15, borderStyle: 'dashed' }}>
              <div className="row gap-10" style={{ gap: 11 }}>
                <Avatar name={i.email} size={36} color="#B9C3BF" />
                <div className="flex1">
                  <div className="wrapline" style={{ fontSize: 14, fontWeight: 600 }}>{i.email.split('@')[0]}</div>
                  <div className="wrapline small">{i.email}</div>
                  <div className="small mono" style={{ fontSize: 11 }}>convite vale até {fmtDayMon(i.expiresAt)}</div>
                </div>
                <span className="pill upper per none">pendente</span>
              </div>
              <div className="row wrap gap-10" style={{ marginTop: 13, alignItems: 'flex-end' }}>
                <div className="flex1" style={{ minWidth: 150 }}>
                  <div className="k" style={{ marginBottom: 4 }}>Papel</div>
                  {manage ? (
                    <select className="select" style={{ height: 38, fontSize: 13.5 }} value={i.role} onChange={(e) => patchInvite.mutate({ id: i.id, role: e.target.value })}>
                      {info.role === 'owner' && <option value="admin">Admin</option>}
                      <option value="nutricionista">Nutricionista</option>
                      <option value="visualizador">Visualizador</option>
                    </select>
                  ) : (
                    <div style={{ fontSize: 13.5, fontWeight: 500 }}>{i.roleLabel}</div>
                  )}
                </div>
                {manage && <Button size="xs" style={{ height: 38 }} loading={resend.isPending && resend.variables?.id === i.id} onClick={() => resend.mutate(i)}>Reenviar</Button>}
                {manage && <Button size="xs" style={{ height: 38 }} onClick={() => revoke.mutate(i.id)}>Revogar</Button>}
              </div>
              {i.role !== 'admin' && (
                <div className="divider-top" style={{ marginTop: 12, paddingTop: 12 }}>
                  <div className="k" style={{ marginBottom: 7 }}>Restaurantes que ela poderá ver</div>
                  <AccessChips ids={i.restaurantIds} disabled={!manage} onChange={(ids) => patchInvite.mutate({ id: i.id, restaurantIds: ids })} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <PlatformCard tid={tid} info={info} members={team.data?.members ?? []} />

      <InviteSheet tid={tid} open={inviting} onClose={() => setInviting(false)} restaurants={rests.data ?? []} ownerView={info.role === 'owner'} onSent={refresh} />
      {confirm.node}
    </div>
  );
}

function InviteSheet({ tid, open, onClose, restaurants, ownerView, onSent }: { tid: string; open: boolean; onClose: () => void; restaurants: RestaurantRow[]; ownerView: boolean; onSent: () => void }) {
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Exclude<Role, 'owner'>>('nutricionista');
  const [access, setAccess] = useState<string[]>([]);
  const [devUrl, setDevUrl] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: () => api.post<{ devUrl?: string }>(`/api/t/${tid}/members/invites`, { email: email.trim(), role, restaurantIds: role === 'admin' ? [] : access }),
    onSuccess: (r) => {
      toast(`Convite enviado para ${email.trim()}`);
      onSent();
      if (r.devUrl) setDevUrl(r.devUrl);
      else close();
    },
    onError: (e) => toast(errMsg(e), 'err'),
  });
  const close = () => { setEmail(''); setAccess([]); setRole('nutricionista'); setDevUrl(null); onClose(); };
  const roles = (Object.keys(ROLE_DESC) as Array<Exclude<Role, 'owner'>>).filter((r) => r !== 'admin' || ownerView);
  return (
    <Sheet open={open} onClose={close} title="Convidar parceira">
      {devUrl ? (
        <>
          <div className="ok-box" style={{ marginBottom: 12 }}>Convite criado. Em desenvolvimento o e-mail é só registrado no console; use o link abaixo para testar.</div>
          <input className="input mono" readOnly value={devUrl} onFocus={(e) => e.target.select()} style={{ fontSize: 12 }} />
          <Button variant="dark" size="lg" block style={{ marginTop: 14 }} onClick={close}>Concluído</Button>
        </>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); if (!/^\S+@\S+\.\S+$/.test(email.trim())) return toast('Informe um e-mail válido', 'err'); send.mutate(); }}>
          <Field label="E-mail"><input className="input" type="email" inputMode="email" placeholder="parceira@nutri.com.br" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
          <div className="label" style={{ marginBottom: 6 }}>Papel</div>
          <div className="stack gap-6" style={{ marginBottom: 14 }}>
            {roles.map((r) => (
              <button type="button" key={r} className={`radio-card ${role === r ? 'on' : ''}`} onClick={() => setRole(r)} aria-pressed={role === r}>
                <div className="dotr" />
                <div className="flex1"><div className="t">{r === 'admin' ? 'Admin' : r === 'nutricionista' ? 'Nutricionista' : 'Visualizador'}</div><div className="d">{ROLE_DESC[r]}</div></div>
              </button>
            ))}
          </div>
          {role !== 'admin' && (
            <>
              <div className="label" style={{ marginBottom: 7 }}>Restaurantes que ela poderá ver</div>
              <div className="row wrap gap-6" style={{ marginBottom: 18 }}>
                {restaurants.map((r) => (
                  <button type="button" key={r.id} className={`chip toggle ${access.includes(r.id) ? 'on' : ''}`} aria-pressed={access.includes(r.id)} onClick={() => setAccess((a) => (a.includes(r.id) ? a.filter((x) => x !== r.id) : [...a, r.id]))}>{short(r.nome)}</button>
                ))}
                {restaurants.length === 0 && <span className="small">Nenhum restaurante cadastrado ainda.</span>}
              </div>
            </>
          )}
          <Button variant="dark" size="lg" block type="submit" loading={send.isPending}>Enviar convite</Button>
        </form>
      )}
    </Sheet>
  );
}

function PlatformCard({ tid, info, members }: { tid: string; info: TenantInfo; members: Member[] }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const toast = useToast();
  const [nome, setNome] = useState(info.tenant.nome);
  const [transferTo, setTransferTo] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [confirmNome, setConfirmNome] = useState('');
  const confirm = useConfirm();
  const me = members.find((m) => m.isMe);

  const rename = useMutation({
    mutationFn: () => api.patch(`/api/t/${tid}`, { nome: nome.trim() }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tenant', tid] }); qc.invalidateQueries({ queryKey: ['me'] }); toast('Nome atualizado'); },
    onError: (e) => toast(errMsg(e), 'err'),
  });
  const transfer = useMutation({
    mutationFn: () => api.post(`/api/t/${tid}/transfer`, { membershipId: transferTo }),
    onSuccess: () => { qc.invalidateQueries(); toast('Propriedade transferida'); },
    onError: (e) => toast(errMsg(e), 'err'),
  });
  const del = useMutation({
    mutationFn: () => api.del(`/api/t/${tid}`, { confirmNome }),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ['me'] }); nav('/', { replace: true }); },
    onError: (e) => toast(errMsg(e), 'err'),
  });
  const leave = async () => {
    if (!me || !(await confirm.ask({ title: 'Sair desta plataforma?', message: 'Você perde o acesso aos restaurantes e relatórios daqui. Para voltar, precisa de um novo convite.', cta: 'Sair', danger: true }))) return;
    try {
      await api.del(`/api/t/${tid}/members/${me.id}`);
      await qc.invalidateQueries({ queryKey: ['me'] });
      nav('/', { replace: true });
    } catch (e) {
      toast(errMsg(e), 'err');
    }
  };

  const candidates = members.filter((m) => !m.isMe);
  return (
    <section className="card" style={{ marginTop: 24 }}>
      <h2 className="h2" style={{ marginBottom: 12 }}>Plataforma</h2>
      {info.can.manageMembers ? (
        <form className="row wrap gap-8" style={{ alignItems: 'flex-end' }} onSubmit={(e) => { e.preventDefault(); rename.mutate(); }}>
          <label className="stack gap-6 flex1" style={{ minWidth: 220, cursor: 'default' }}>
            <span className="label">Nome</span>
            <input className="input sm" value={nome} onChange={(e) => setNome(e.target.value)} />
          </label>
          <Button size="sm" type="submit" style={{ height: 42 }} loading={rename.isPending} disabled={nome.trim() === info.tenant.nome || nome.trim().length < 3}>Salvar</Button>
        </form>
      ) : (
        <div style={{ fontSize: 13.5 }}>{info.tenant.nome}</div>
      )}

      {info.can.manageTenant && (
        <>
          <div className="divider-top" style={{ marginTop: 16, paddingTop: 14 }}>
            <div className="label" style={{ marginBottom: 4 }}>Transferir propriedade</div>
            <div className="small" style={{ marginBottom: 8, lineHeight: 1.5 }}>A nova proprietária passa a controlar a plataforma; você vira Admin.</div>
            <div className="row wrap gap-8">
              <select className="select sm flex1" style={{ minWidth: 200 }} value={transferTo} onChange={(e) => setTransferTo(e.target.value)}>
                <option value="">Escolha um membro</option>
                {candidates.map((m) => <option key={m.id} value={m.id}>{m.nome} · {m.roleLabel}</option>)}
              </select>
              <Button
                size="sm" style={{ height: 42 }} disabled={!transferTo} loading={transfer.isPending}
                onClick={async () => {
                  const m = candidates.find((x) => x.id === transferTo);
                  if (m && (await confirm.ask({ title: 'Transferir propriedade?', message: `${m.nome} vira proprietária de ${info.tenant.nome}.`, cta: 'Transferir', danger: true }))) transfer.mutate();
                }}
              >
                Transferir
              </Button>
            </div>
          </div>
          <div className="divider-top" style={{ marginTop: 16, paddingTop: 14 }}>
            <div className="label" style={{ marginBottom: 4, color: 'var(--nc-text)' }}>Excluir plataforma</div>
            <div className="small" style={{ marginBottom: 8, lineHeight: 1.5 }}>Apaga restaurantes, visitas, fotos e relatórios de forma permanente.</div>
            <Button variant="danger" size="sm" onClick={() => setDeleting(true)}>Excluir plataforma</Button>
          </div>
        </>
      )}
      {info.role !== 'owner' && (
        <div className="divider-top" style={{ marginTop: 16, paddingTop: 14 }}>
          <Button variant="danger" size="sm" onClick={leave}>Sair desta plataforma</Button>
        </div>
      )}

      <Sheet open={deleting} onClose={() => setDeleting(false)} title="Excluir plataforma">
        <div className="sub" style={{ marginBottom: 14 }}>Esta ação não pode ser desfeita. Para confirmar, digite <strong style={{ color: 'var(--text)' }}>{info.tenant.nome}</strong>.</div>
        <input className="input" value={confirmNome} onChange={(e) => setConfirmNome(e.target.value)} placeholder={info.tenant.nome} />
        <Button variant="danger-solid" size="lg" block style={{ marginTop: 14 }} disabled={confirmNome.trim() !== info.tenant.nome} loading={del.isPending} onClick={() => del.mutate()}>Excluir definitivamente</Button>
      </Sheet>
      {confirm.node}
    </section>
  );
}
